/**
 * ============================================
 * HomePage (v3)
 * ============================================
 *
 * Redesigned home page with role-aware sections:
 * 1) Welcome Banner + Date + Pulse Dot
 * 2) KPI Strip (role-differentiated, with sparklines)
 * 3) Live Activity (TENANT_ADMIN+): channel bar chart + trend area chart
 * 4) Workspace Category Cards
 * 5) Bottom Row: Announcements + Getting Started Guide
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../lib/hooks/useAuth';
import { useTab } from '../contexts/TabContext';
import { defaultAnnouncementAPI } from '../lib/services/announcementApi';
import { defaultMonitoringAPI } from '../lib/services/monitoringApi';
import {
  getOverview,
  getUsageTrend,
  getActiveSessionStats,
  defaultAnalyticsAPI,
} from '../lib/services/analyticsApi';
import { defaultToast } from '../lib/utils/toast';
import { showError } from '../lib/services/errorService';
import { logger } from '../lib/utils/logger';
import { AnnouncementDetailModal } from '../components/announcement/AnnouncementDetailModal';
import MiniSparkline from '../components/admin/charts/echarts/MiniSparkline';
import HorizontalBarChart from '../components/admin/charts/echarts/HorizontalBarChart';
import DualAxisAreaChart from '../components/admin/charts/echarts/DualAxisAreaChart';
import {
  HOME_TEXT,
  WORKSPACE_CATEGORIES,
  GETTING_STARTED_GUIDES,
  ANNOUNCEMENT_TYPE_LABELS,
  ANNOUNCEMENT_TYPE_CLASSES,
} from '../constants/homeMessages';
import { ROLE_DISPLAY_NAMES } from '../constants/authMessages';
import './HomePage.css';

// ============================================
// Role hierarchy for visibility filtering
// ============================================
const ROLE_LEVEL = {
  USER: 1,
  TENANT_ADMIN: 2,
  GLOBAL_ADMIN: 3,
};

function hasMinRole(userRole, minRole) {
  return (ROLE_LEVEL[userRole] || 0) >= (ROLE_LEVEL[minRole] || 0);
}

/**
 * Get display name from user object
 */
function getUserDisplayName(user) {
  if (!user) return HOME_TEXT.WELCOME_GUEST;
  if (user.name) return user.name;
  if (user.email) return user.email.split('@')[0];
  return HOME_TEXT.WELCOME_GUEST;
}

/**
 * Format date string to Korean locale
 */
function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date
    .toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    .replace(/\. /g, '-')
    .replace('.', '');
}

/**
 * Check if announcement is recent (within 7 days)
 */
function isNewAnnouncement(createdAt) {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  const now = new Date();
  const diffDays = (now - created) / (1000 * 60 * 60 * 24);
  return diffDays <= 7;
}

/**
 * Format number with Korean locale
 */
function formatNum(n) {
  if (n == null) return '—';
  return typeof n === 'number' ? n.toLocaleString('ko-KR') : n;
}

// ============================================
// SectionLabel Component
// ============================================

function SectionLabel({ label }) {
  return (
    <div className="home-section-divider">
      <span className="home-section-divider__text">{label}</span>
      <span className="home-section-divider__line" />
    </div>
  );
}

// ============================================
// WorkspaceCard Component
// ============================================

function WorkspaceCard({ category, onNavigate }) {
  return (
    <div className={`home-workspace-card home-workspace-${category.key}`}>
      <div className="home-workspace-card-header">
        <div className="home-workspace-card-icon">
          <CategoryIcon type={category.key} />
        </div>
        <div>
          <h3>{category.title}</h3>
          <p>{category.description}</p>
        </div>
      </div>
      <div className="home-workspace-card-chips">
        {category.items.map((item) => (
          <button
            key={item.path}
            className="home-workspace-chip"
            onClick={() => onNavigate(item.path)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================
// GuideItem Component
// ============================================

function GuideItem({ guide, onNavigate }) {
  return (
    <button className="home-guide-item" onClick={() => onNavigate(guide.path)}>
      <span className="home-guide-step">{guide.step}</span>
      <div className="home-guide-content">
        <h4>{guide.title}</h4>
        <p>{guide.description}</p>
      </div>
      <ChevronRightIcon />
    </button>
  );
}

// ============================================
// HomePage Component
// ============================================

export function HomePage() {
  const { user, loading, isGlobalAdmin, isTenantAdminOrHigher } = useAuth();
  const { openTab } = useTab();
  const displayName = getUserDisplayName(user);
  const userRole = user?.role || 'USER';

  // Existing state
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // New dashboard state
  const [dashboardStats, setDashboardStats] = useState(null);
  const [overview, setOverview] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [channelSessionData, setChannelSessionData] = useState([]);
  const [escalationSummary, setEscalationSummary] = useState(null);
  const [, setStatsLoading] = useState(true);

  const isTenantAdmin = isTenantAdminOrHigher();
  const isGlobalAdminUser = isGlobalAdmin();

  const handleNavigate = (path) => {
    openTab(path);
  };

  const handleAnnouncementClick = (announcement) => {
    setSelectedAnnouncement(announcement);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAnnouncement(null);
  };

  // Fetch announcements
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        setAnnouncementsLoading(true);
        const data = await defaultAnnouncementAPI.getActiveAnnouncements();
        setAnnouncements(data);
      } catch (error) {
        logger.error('Failed to fetch announcements:', error);
        showError(error, '공지사항을 불러오지 못했습니다.');
      } finally {
        setAnnouncementsLoading(false);
      }
    };
    fetchAnnouncements();
  }, []);

  // Fetch dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      setStatsLoading(true);
      const today = new Date().toISOString().slice(0, 10);
      const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);

      const calls = [getOverview()];
      if (isTenantAdmin) {
        calls.push(
          defaultMonitoringAPI.getDashboardStats(),
          getUsageTrend(weekAgo, today),
          getActiveSessionStats(),
          defaultAnalyticsAPI.getEscalationStats(weekAgo, today)
        );
      }

      const results = await Promise.allSettled(calls);
      if (results[0].status === 'fulfilled') setOverview(results[0].value);
      if (isTenantAdmin) {
        if (results[1]?.status === 'fulfilled') setDashboardStats(results[1].value);
        if (results[2]?.status === 'fulfilled') {
          const trends = results[2].value;
          setTrendData(trends?.trends || trends?.daily || (Array.isArray(trends) ? trends : []));
        }
        if (results[3]?.status === 'fulfilled') setChannelSessionData(results[3].value || []);
        if (results[4]?.status === 'fulfilled')
          setEscalationSummary(results[4].value?.summary || null);
      }
    } catch (err) {
      logger.error('Home dashboard data error:', err);
      showError(err, '대시보드 데이터를 불러오지 못했습니다.');
    } finally {
      setStatsLoading(false);
    }
  }, [isTenantAdmin]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // 60s polling
  const pollingRef = useRef(null);
  useEffect(() => {
    pollingRef.current = setInterval(loadDashboardData, 60000);
    return () => clearInterval(pollingRef.current);
  }, [loadDashboardData]);

  // Derived data
  const sparkConversations = useMemo(() => trendData.map((d) => d.conversations || 0), [trendData]);

  const channelBarData = useMemo(() => {
    if (!Array.isArray(channelSessionData)) return [];
    return channelSessionData
      .filter((s) => s.channelId != null)
      .map((s) => ({
        name: s.channelName || `Channel ${s.channelId}`,
        value: s.activeSessionCount || 0,
      }));
  }, [channelSessionData]);

  const chartTrendData = useMemo(() => {
    if (!Array.isArray(trendData) || trendData.length === 0) return [];
    return trendData.map((d) => ({
      date: d.date || '',
      conversations: d.conversations || 0,
      tokens: d.tokens || d.totalTokens || 0,
    }));
  }, [trendData]);

  // Filter workspace categories by role
  const visibleCategories = WORKSPACE_CATEGORIES.filter((cat) => hasMinRole(userRole, cat.minRole))
    .map((cat) => ({
      ...cat,
      items: cat.items.filter((item) => hasMinRole(userRole, item.minRole)),
    }))
    .filter((cat) => cat.items.length > 0);

  // Today's date string
  const todayStr = new Date().toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'short',
  });

  if (loading) {
    return (
      <div className="home-page">
        <div className="home-loading">{HOME_TEXT.LABEL_LOADING}</div>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* 1) Welcome Banner */}
      <section className="home-welcome">
        <div className="home-welcome-content">
          <h1>
            {HOME_TEXT.WELCOME_TITLE}, {displayName}
            {HOME_TEXT.WELCOME_SUFFIX}
          </h1>
          <p>{HOME_TEXT.WELCOME_SUBTITLE}</p>
        </div>
        <div className="home-welcome-meta">
          <span className="home-welcome-date">{todayStr}</span>
          <span className="home-pulse-dot" />
          <span className="home-role-badge">{ROLE_DISPLAY_NAMES[userRole] || userRole}</span>
        </div>
      </section>

      {/* 2) KPI Strip */}
      <section className="home-section">
        <SectionLabel label={HOME_TEXT.SECTION_KPI} />
        <div className="home-kpi-strip">
          {/* Total conversations */}
          <div
            className="home-kpi-card"
            onClick={() => handleNavigate(isTenantAdmin ? '/admin/conversations' : '/chat')}
          >
            <div className="home-kpi-card__icon home-kpi-card__icon--teal">
              <ChatIcon />
            </div>
            <div className="home-kpi-card__body">
              <span className="home-kpi-card__label">
                {isTenantAdmin ? '활성 상담' : '내 대화'}
              </span>
              <span className="home-kpi-card__value">
                {isTenantAdmin
                  ? formatNum(dashboardStats?.activeSessions)
                  : formatNum(overview?.totalConversations)}
              </span>
            </div>
            {sparkConversations.length > 1 && (
              <div className="home-kpi-card__spark">
                <MiniSparkline data={sparkConversations} color="#0d9488" width={64} height={24} />
              </div>
            )}
          </div>

          {/* Escalation count (TENANT_ADMIN+) or Token usage (USER) */}
          {isTenantAdmin ? (
            <div className="home-kpi-card" onClick={() => handleNavigate('/admin/escalations')}>
              <div className="home-kpi-card__icon home-kpi-card__icon--amber">
                <EscalationIcon />
              </div>
              <div className="home-kpi-card__body">
                <span className="home-kpi-card__label">주간 에스컬레이션</span>
                <span className="home-kpi-card__value">
                  {formatNum(escalationSummary?.totalCount)}
                </span>
              </div>
            </div>
          ) : (
            <div className="home-kpi-card" onClick={() => handleNavigate('/chat')}>
              <div className="home-kpi-card__icon home-kpi-card__icon--amber">
                <TokenIcon />
              </div>
              <div className="home-kpi-card__body">
                <span className="home-kpi-card__label">토큰 사용량</span>
                <span className="home-kpi-card__value">{formatNum(overview?.totalTokens)}</span>
              </div>
            </div>
          )}

          {/* Avg escalations per day (TENANT_ADMIN+) or Active models (USER) */}
          {isTenantAdmin ? (
            <div className="home-kpi-card" onClick={() => handleNavigate('/admin/escalations')}>
              <div className="home-kpi-card__icon home-kpi-card__icon--indigo">
                <ChartIcon />
              </div>
              <div className="home-kpi-card__body">
                <span className="home-kpi-card__label">일평균 에스컬레이션</span>
                <span className="home-kpi-card__value">
                  {escalationSummary?.avgPerDay != null
                    ? escalationSummary.avgPerDay.toFixed(1)
                    : '—'}
                </span>
              </div>
            </div>
          ) : (
            <div className="home-kpi-card" onClick={() => handleNavigate('/chat')}>
              <div className="home-kpi-card__icon home-kpi-card__icon--indigo">
                <ModelIcon />
              </div>
              <div className="home-kpi-card__body">
                <span className="home-kpi-card__label">활성 모델</span>
                <span className="home-kpi-card__value">{formatNum(overview?.activeModels)}</span>
              </div>
            </div>
          )}

          {/* Admin-only KPIs */}
          {isTenantAdmin && (
            <div className="home-kpi-card" onClick={() => handleNavigate('/admin/sessions')}>
              <div className="home-kpi-card__icon home-kpi-card__icon--navy">
                <SessionIcon />
              </div>
              <div className="home-kpi-card__body">
                <span className="home-kpi-card__label">실시간 세션</span>
                <span className="home-kpi-card__value">
                  {formatNum(dashboardStats?.activeSessions)}
                </span>
              </div>
            </div>
          )}

          {isGlobalAdminUser && (
            <>
              <div className="home-kpi-card" onClick={() => handleNavigate('/admin/tenants')}>
                <div className="home-kpi-card__icon home-kpi-card__icon--violet">
                  <TenantIcon />
                </div>
                <div className="home-kpi-card__body">
                  <span className="home-kpi-card__label">테넌트</span>
                  <span className="home-kpi-card__value">{formatNum(overview?.totalTenants)}</span>
                </div>
              </div>
              <div className="home-kpi-card" onClick={() => handleNavigate('/admin/users')}>
                <div className="home-kpi-card__icon home-kpi-card__icon--rose">
                  <UserIcon />
                </div>
                <div className="home-kpi-card__body">
                  <span className="home-kpi-card__label">사용자</span>
                  <span className="home-kpi-card__value">{formatNum(overview?.totalUsers)}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* 3) Live Activity (TENANT_ADMIN+) */}
      {isTenantAdmin && (
        <section className="home-section">
          <SectionLabel label={HOME_TEXT.SECTION_LIVE_ACTIVITY} />
          <div className="home-live-section">
            {/* Channel sessions bar chart */}
            <div className="home-live-card">
              <div className="home-live-card__header">
                <span className="home-live-card__title">{HOME_TEXT.LABEL_CHANNEL_STATUS}</span>
                <button
                  className="home-live-card__action"
                  onClick={() => handleNavigate('/admin/channels')}
                >
                  {HOME_TEXT.LABEL_VIEW_ANALYSIS}
                </button>
              </div>
              <div className="home-live-card__body">
                {channelBarData.length > 0 ? (
                  <HorizontalBarChart data={channelBarData} height={200} color="#2d3a52" />
                ) : (
                  <div className="home-announcement-empty">{HOME_TEXT.LABEL_NO_ACTIVITY}</div>
                )}
              </div>
            </div>

            {/* Trend area chart */}
            <div className="home-live-card">
              <div className="home-live-card__header">
                <span className="home-live-card__title">주간 추이</span>
                <button
                  className="home-live-card__action"
                  onClick={() => handleNavigate('/admin/service-analytics')}
                >
                  {HOME_TEXT.LABEL_VIEW_ALL}
                </button>
              </div>
              <div className="home-live-card__body">
                {chartTrendData.length > 0 ? (
                  <DualAxisAreaChart data={chartTrendData} height={200} />
                ) : (
                  <div className="home-announcement-empty">{HOME_TEXT.LABEL_NO_ACTIVITY}</div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 4) Workspace Category Cards */}
      <section className="home-section">
        <SectionLabel label={HOME_TEXT.SECTION_WORKSPACE} />
        <div className="home-workspace-grid">
          {visibleCategories.map((category) => (
            <WorkspaceCard key={category.key} category={category} onNavigate={handleNavigate} />
          ))}
        </div>
      </section>

      {/* 6) Bottom Row: Announcements + Guide */}
      <div className="home-bottom-grid">
        {/* Announcements */}
        <section className="home-section">
          <div className="home-card">
            <div className="home-card-header">
              <AnnouncementIcon />
              <h3>{HOME_TEXT.SECTION_ANNOUNCEMENTS}</h3>
              <button className="home-card-more" onClick={() => handleNavigate('/announcements')}>
                {HOME_TEXT.LABEL_VIEW_ALL} <ArrowRightIcon />
              </button>
            </div>
            <div className="home-card-body">
              {announcementsLoading ? (
                <div className="home-announcement-loading">{HOME_TEXT.LABEL_LOADING}</div>
              ) : announcements.length === 0 ? (
                <div className="home-announcement-empty">{HOME_TEXT.LABEL_NO_ACTIVITY}</div>
              ) : (
                <div className="home-announcement-list">
                  {announcements.slice(0, 5).map((item) => (
                    <div
                      key={item.id}
                      className={`home-announcement-item clickable ${item.isPinned ? 'pinned' : ''}`}
                      onClick={() => handleAnnouncementClick(item)}
                    >
                      <div className="home-announcement-header">
                        {item.isPinned && (
                          <span className="home-announcement-badge badge-pinned">
                            <PinIcon />
                          </span>
                        )}
                        <span
                          className={`home-announcement-badge ${ANNOUNCEMENT_TYPE_CLASSES[item.type] || 'badge-info'}`}
                        >
                          {ANNOUNCEMENT_TYPE_LABELS[item.type] || '안내'}
                        </span>
                        {(item.isNew || isNewAnnouncement(item.createdAt)) && (
                          <span className="home-announcement-badge badge-new">NEW</span>
                        )}
                        <span className="home-announcement-title">{item.title}</span>
                      </div>
                      <div className="home-announcement-content">{item.content}</div>
                      <div className="home-announcement-date">
                        {item.date || formatDate(item.createdAt)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Right column: Getting Started guide (all roles) + manual link */}
        <section className="home-section">
          <div className="home-card">
            <div className="home-card-header">
              <GuideIcon />
              <h3>{HOME_TEXT.SECTION_GETTING_STARTED}</h3>
              <button
                className="home-card-more"
                onClick={() => {
                  const w = 1200,
                    h = 800;
                  const left = (window.screen.width - w) / 2;
                  const top = (window.screen.height - h) / 2;
                  window.open(
                    '/workflow-manual',
                    'workflow-manual-popup',
                    `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`
                  );
                }}
              >
                사용자 매뉴얼 <ArrowRightIcon />
              </button>
            </div>
            <div className="home-card-body home-guide-list">
              {GETTING_STARTED_GUIDES.map((guide) => (
                <GuideItem key={guide.id} guide={guide} onNavigate={handleNavigate} />
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Announcement Detail Modal */}
      <AnnouncementDetailModal
        announcement={selectedAnnouncement}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
}

// ============================================
// Icons
// ============================================

const ChatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155"
    />
  </svg>
);

const TokenIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const ModelIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
    />
  </svg>
);

const SessionIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
    />
  </svg>
);

const TenantIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"
    />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
    />
  </svg>
);

const EscalationIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
    />
  </svg>
);

const ChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
    />
  </svg>
);

const CategoryIcon = ({ type }) => {
  switch (type) {
    case 'builder':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21.75 6.75a4.5 4.5 0 01-4.884 4.484c-1.076-.091-2.264.071-2.95.904l-7.152 8.684a2.548 2.548 0 11-3.586-3.586l8.684-7.152c.833-.686.995-1.874.904-2.95a4.5 4.5 0 016.336-4.486l-3.276 3.276a3.004 3.004 0 002.25 2.25l3.276-3.276c.256.565.398 1.192.398 1.852z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4.867 19.125h.008v.008h-.008v-.008z"
          />
        </svg>
      );
    case 'labs':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M5 14.5l-.94.94a1.5 1.5 0 00.44 2.428l.56.224a8.25 8.25 0 007.18.112l.264-.113a1.875 1.875 0 011.68.075l.274.164a1.5 1.5 0 001.378.06l1.22-.488A1.5 1.5 0 0019.8 15.3M5 14.5l2.25 2.25"
          />
        </svg>
      );
    case 'monitoring':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
          />
        </svg>
      );
    case 'admin':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.32 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      );
    default:
      return null;
  }
};

const AnnouncementIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m3 11 18-5v12L3 14v-3z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </svg>
);

const GuideIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
    />
  </svg>
);

const PinIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="17" x2="12" y2="22" />
    <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    className="home-chevron-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export default HomePage;
